/**
 * Optimiza URLs de Supabase Storage usando el endpoint de renderizado de imágenes
 * Convierte URLs de storage a URLs optimizadas con redimensionamiento y compresión
 */

import { env } from "@/lib/env";
import { logger } from "./logger";

const SUPABASE_PUBLIC_PATH = "/storage/v1/object/public/";
const SUPABASE_RENDER_PATH = "/storage/v1/render/image/public/";

/**
 * Convierte una URL de Supabase render (/render/image/public/...) a URL pública directa
 * (/object/public/...). Evita fallos cuando Image Transformation no está disponible.
 * Si la URL no es de render, devuelve la URL absoluta normalizada.
 */
/** Normaliza URLs de Supabase que usan bucket erróneo AVATARS → media/avatars */
function normalizeSupabaseStorageUrl(url: string): string {
  if (url.includes("/storage/v1/object/public/AVATARS/")) {
    return url.replace("/storage/v1/object/public/AVATARS/", "/storage/v1/object/public/media/avatars/");
  }
  return url;
}

export function toDirectPublicStorageUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  const u = String(url).trim();
  if (!u) return undefined;
  try {
    const normalized = normalizeSupabaseStorageUrl(u);
    if (normalized.includes(SUPABASE_RENDER_PATH)) {
      const pathPart = normalized.split(SUPABASE_RENDER_PATH)[1]?.split("?")[0];
      if (pathPart) {
        const origin = normalized.startsWith("http") ? new URL(normalized).origin : getSupabaseUrl();
        return `${origin}${SUPABASE_PUBLIC_PATH}${pathPart}`;
      }
    }
    return ensureAbsoluteImageUrl(normalized) ?? normalized;
  } catch {
    return normalizeSupabaseStorageUrl(u);
  }
}

/**
 * Obtiene la URL base de Supabase desde las variables de entorno
 * Si no está disponible, intenta extraerla de una URL existente
 */
function getSupabaseUrl(): string {
  try {
    // Intentar obtener desde las variables de entorno primero
    if (env.supabase.url) {
      return env.supabase.url;
    }
    
    // Si estamos en el navegador, intentar extraer de la URL actual
    if (typeof window !== "undefined") {
      // Si la URL actual contiene el path de storage, extraer el origen
      const currentUrl = window.location.href;
      const match = currentUrl.match(/^(https?:\/\/[^\/]+)/);
      if (match) {
        return match[1];
      }
    }
  } catch (error) {
    logger.warn('[getSupabaseUrl] Error obteniendo URL de Supabase:', error);
  }
  return "";
}

/**
 * Optimiza una URL de Supabase Storage usando el endpoint de renderizado
 * Solo optimiza URLs de Supabase Storage, deja otras URLs sin cambios
 * 
 * @param url - URL a optimizar (puede ser una URL completa o una ruta relativa)
 * @param width - Ancho deseado (default: 600)
 * @param height - Alto deseado (default: 400)
 * @param quality - Calidad de compresión (default: 80)
 * @param extra - format webp/avif y resize (cover para cards tipo object-fit:cover)
 * @returns URL optimizada o la URL original si no es de Supabase Storage
 */
export type SupabaseImageTransformExtras = {
  format?: "webp" | "avif";
  /** default: contain (compat); cover alinea con object-fit: cover en cards */
  resize?: "cover" | "contain";
};

export function optimizeSupabaseImageUrl(
  url?: string | null,
  width: number = 600,
  height: number = 400,
  quality: number = 80,
  extra?: SupabaseImageTransformExtras
): string | undefined {
  if (!url) return undefined;
  
  const urlString = String(url).trim();
  
  // Si ya es una URL completa de renderizado, no hacer nada
  if (urlString.includes(SUPABASE_RENDER_PATH)) {
    return urlString;
  }
  
  // Si no es una URL HTTP/HTTPS y no empieza con /, puede ser una ruta relativa
  // Intentar construir la URL completa
  let fullUrl: string;
  
  try {
    // Si ya es una URL completa
    if (/^https?:\/\//i.test(urlString)) {
      fullUrl = urlString;
    } else if (urlString.startsWith('/')) {
      // Si es una ruta absoluta, construir con el origen actual
      fullUrl = typeof window !== "undefined" 
        ? `${window.location.origin}${urlString}`
        : `${getSupabaseUrl()}${urlString}`;
    } else {
      // Si es una ruta relativa tipo "bucket/path", convertir a URL pública primero
      // Esto requiere acceso a supabase, pero para optimización podemos intentar construirla
      const supabaseBase = getSupabaseUrl();
      if (supabaseBase) {
        // Asumir que es una ruta de storage y construir la URL pública
        fullUrl = `${supabaseBase}${SUPABASE_PUBLIC_PATH}${urlString}`;
      } else {
        // Si no podemos construir la URL, retornar original
        return urlString;
      }
    }
    
    // Verificar si es una URL de Supabase Storage
    const urlObj = new URL(fullUrl);
    if (!urlObj.pathname.includes(SUPABASE_PUBLIC_PATH)) {
      // No es una URL de Supabase Storage, retornar original
      return urlString;
    }
    
    // Extraer la ruta del archivo (bucket/path)
    const publicKey = urlObj.pathname.split(SUPABASE_PUBLIC_PATH)[1];
    if (!publicKey) {
      return urlString;
    }
    
    // Construir la URL de renderizado optimizada
    const renderUrl = `${urlObj.origin}${SUPABASE_RENDER_PATH}${publicKey}`;
    const params = new URLSearchParams();
    params.set("width", String(width));
    params.set("height", String(height));
    params.set("resize", extra?.resize ?? "contain");
    params.set("quality", String(quality));
    if (extra?.format) params.set("format", extra.format);
    
    return `${renderUrl}?${params.toString()}`;
  } catch (error) {
    // Si hay algún error al procesar la URL, retornar la original
    logger.warn('[optimizeSupabaseImageUrl] Error procesando URL:', urlString, error);
    return urlString;
  }
}

/**
 * Convierte una URL relativa (ej. "bucket/path") a absoluta usando la base de Supabase.
 * Si no hay base o la URL es inválida, retorna null.
 */
export function ensureAbsoluteImageUrl(url?: string | null): string | null {
  if (!url) return null;
  let urlString = String(url).trim();
  if (!urlString) return null;
  urlString = normalizeSupabaseStorageUrl(urlString);
  if (/^https:\/\//i.test(urlString)) return urlString;
  if (/^http:\/\//i.test(urlString)) {
    if (typeof window !== "undefined" && window.location?.protocol === "https:") {
      logger.warn("[ensureAbsoluteImageUrl] HTTP bloqueado en página HTTPS:", urlString.slice(0, 80));
      return null;
    }
    return urlString;
  }
  if (urlString.startsWith("/")) {
    const base = getSupabaseUrl();
    if (base) return `${base.replace(/\/$/, "")}${urlString}`;
    if (typeof window !== "undefined") return `${window.location.origin}${urlString}`;
    return null;
  }
  const base = getSupabaseUrl();
  if (!base) {
    logger.warn("[ensureAbsoluteImageUrl] URL relativa sin base:", urlString.slice(0, 60));
    return null;
  }
  // Si viene como "avatars/..." (path sin bucket), asumir bucket "media" por defecto.
  // Esto evita generar URLs inválidas como /storage/.../public/avatars/... cuando el bucket real es "media".
  const isKnownBucketPrefix =
    urlString.startsWith("media/") || urlString.startsWith("public/");
  const storageKey = isKnownBucketPrefix ? urlString : `media/${urlString}`;
  const full = `${base.replace(/\/$/, "")}${SUPABASE_PUBLIC_PATH}${storageKey}`;
  return full;
}

/**
 * Normaliza una URL (compatibilidad con funciones normalizeUrl existentes)
 * y opcionalmente la optimiza si es de Supabase Storage.
 * URLs relativas se convierten a absolutas con la base de Supabase; si no hay base, retorna undefined.
 *
 * @param url - URL a normalizar
 * @param optimize - Si true, optimiza URLs de Supabase Storage (default: true)
 * @returns URL normalizada y/o optimizada, o undefined si es relativa sin base o http bloqueado
 */
export function normalizeAndOptimizeUrl(
  url?: string | null,
  optimize: boolean = true
): string | undefined {
  if (!url) return undefined;

  const urlString = String(url).trim();
  if (!urlString) return undefined;

  // Si ya es una URL HTTP/HTTPS o ruta absoluta, normalizarla
  if (/^https?:\/\//i.test(urlString) || urlString.startsWith("/")) {
    const absolute = ensureAbsoluteImageUrl(urlString);
    if (!absolute) return undefined;
    return optimize ? optimizeSupabaseImageUrl(absolute) || absolute : absolute;
  }

  // Placeholder patterns
  if (/^\d+x\d+(\/.*)?$/i.test(urlString)) {
    return `https://via.placeholder.com/${urlString}`;
  }
  if (/^[0-9A-Fa-f]{6}(\/|\?).*/.test(urlString)) {
    return `https://via.placeholder.com/800x400/${urlString}`;
  }

  // Ruta relativa (ej. bucket/path): convertir a absoluta primero
  const absolute = ensureAbsoluteImageUrl(urlString);
  if (!absolute) return undefined;
  if (optimize) {
    const optimized = optimizeSupabaseImageUrl(absolute);
    return optimized || absolute;
  }
  return absolute;
}

